import React, { useEffect, useState } from 'react'
import { cn } from 'cn'
import { ExclamationCircleIcon } from '@heroicons/react/24/solid'
import useDeepCompareEffect from 'hooks/useDeepCompareEffect'
import FieldDescription from 'components/ui/FieldDescription'
import FieldError from 'components/ui/FieldError'

export type Props = {
  name: string
  label?: string
  description?: string
  placeholder?: string
  error?: string | null
  value: Date | null
  baseDate: Date
  fullWidth?: boolean
  onChange: (value: Date | null) => void
  disabled?: boolean
  newDesign?: boolean
}

const getTimeString = (date: Date | null) => {
  if (date) {
    const hours = date.getHours() < 10 ? `0${date.getHours()}` : date.getHours()
    const minutes =
      date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes()
    return `${hours}:${minutes}`
  }
  return ''
}

export default function TimeField({
  label,
  name,
  description,
  placeholder,
  error,
  baseDate,
  disabled,
  fullWidth,
  value,
  onChange,
  newDesign,
  ...inputProps
}: Props) {
  const [innerValue, setInnerValue] = useState<string>(getTimeString(value))

  const onChangeRef = React.useRef(onChange)

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  useDeepCompareEffect(() => {
    if (innerValue) {
      const [hours, minutes] = innerValue.split(':').map(Number)
      const newDate = new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        baseDate.getDate(),
        hours,
        minutes,
        0
      )
      if (Number.isNaN(newDate.getTime())) {
        onChangeRef.current(null)
        return
      }
      onChangeRef.current(newDate)
    } else {
      onChangeRef.current(null)
    }
  }, [innerValue, baseDate])

  useDeepCompareEffect(() => {
    setInnerValue(getTimeString(value))
  }, [value])

  return (
    <div className={cn('relative', { 'w-full': fullWidth })}>
      {label && (
        <label
          className={cn('block', {
            'text-base font-medium text-gray-700': !newDesign,
            'font-theme-heading font-bold text-gray-900': newDesign
          })}
          htmlFor={name}
        >
          {label}
        </label>
      )}
      <FieldDescription newDesign={newDesign}>{description}</FieldDescription>
      <div className="relative">
        <input
          className={cn(
            'w-full h-[42px] mt-1 border border-solid border-gray-300 p-2 outline-none-mouse focus:ring-orange-500 focus:border-orange-500 shadow-xs rounded-xl scroll-offset',
            {
              'text-gray-500': disabled,
              'ring-red-500 border-red-500': error
            }
            // inputProps.className
          )}
          onChange={(e) => setInnerValue(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          id={name}
          type="time"
          value={innerValue ?? ''}
          {...inputProps}
        />
        {error && (
          <div className="absolute inset-y-0 right-6 md:right-8 top-1 flex items-center pointer-events-none">
            <ExclamationCircleIcon
              aria-hidden="true"
              className="h-5 w-5 text-red-500"
            />
          </div>
        )}
      </div>
      <FieldError>{error}</FieldError>
    </div>
  )
}
